import { Component, EventEmitter, Input, Output } from '@angular/core';

import * as d3Scale from 'd3-scale';
import * as d3Array from 'd3-array';
import * as d3Shape from 'd3-shape';
import * as d3Axis from 'd3-axis';

import { DataColumnPoint, TooltipData, ICoordinates, Serie, DataColumn, Config, TooltipValue } from '../../../models';
import { BaseChartComponent } from '../../rawd3/base';
import { ChartingService } from '../../../services';

@Component({
    selector: 'aa-stacked-column2',
    templateUrl: './stacked-column2.component.html',
    styleUrls: [
        '../../../container.scss',
        './stacked-column2.component.scss'
    ],
    providers: [
        ChartingService
    ],
    standalone: false
})
export class StackedColumn2Component extends BaseChartComponent<DataColumn>
{
    @Input() keys: string[];
    @Input() stacked100: boolean = true;
    @Input() originZero: boolean = true;
    @Input() cfg: Config;
    @Output() columnClicked: EventEmitter<any> = new EventEmitter<any>();
    public xScale: d3Scale.ScaleBand<string>;
    public yScale: d3Scale.ScaleLinear<number, number>;
    public yScale1: d3Scale.ScaleLinear<number, number>;
    public zScale: d3Scale.ScaleOrdinal<string, unknown>;
    public xFormatTick: (date: any) => string;
    public yFormatTick: (value: number) => string;
    public tooltipData: TooltipData;
    public hovered: boolean = false;
    private _series: Serie[] = undefined;
    public get series (): Serie[]
    {
        if (this._series === undefined)
        {
            this._series = Serie.getSeriesFromDataColumn(this.data);
            // this._series2 = this.series.map((serie: Serie): Serie =>
            //     ({
            //         ...serie,
            //         unit: ''
            //     }));
        }

        return this._series;
    }
    // private _series2: Serie[] = undefined;
    // public get series2 (): Serie[]
    // {
    //     if (this._series2 === undefined)
    //     {
    //         this._series = Serie.getSeriesFromDataColumn(this.data);
    //         this._series2 = this.series.map((serie: Serie): Serie =>
    //             ({
    //                 ...serie,
    //                 unit: ''
    //             }));
    //     }

    //     return this._series2;
    // }
    public tooltipFormatTick: (value: number) => (string) = (value: number): string => value.toFixed(0);
    private eachBand: number;
    private fmt: string;
    private localData: any[];

    constructor (
        private chartingService: ChartingService
    )
    {
        super();
    }

    protected drawChart (): void
    {
        this.chartingService.config = this.cfg;
        this.initSvg();
        this.xFormatTick = this.chartingService.xTimeFormatTick;
        this.yFormatTick = this.chartingService.yFormatTick;

        this.fmt = 's';
        const t: string = 'total';
        this.localData = this.data.points
            .map((p: DataColumnPoint): { x: string; } =>
            {
                const o = { x: p.x };
                let total = 0;

                for (const [, v] of p.values)
                {
                    total += v;
                }

                for (const [k, v] of p.values)
                {
                    if (this.stacked100)
                    {
                        o[k] = 100 * v / total;
                    }
                    else
                    {
                        o[k] = v;
                    }
                }

                if (this.stacked100)
                {
                    o[t] = 100;
                    this.fmt = '%';
                }
                else
                {
                    o[t] = total;
                }

                return o;
            });

        this.xScale.domain(this.localData.map((d: any): any => this.chartingService.config.xAccessor(d.x)));
        this.eachBand = this.xScale.step();

        if (this.stacked100)
        {
            this.yScale.domain([0, 100]).nice();
            this.yScale1.domain([0, 1]).nice();
        }
        else
        {
            this.yScale.domain([0, d3Array.max(this.localData, (d: any): any => d.total)]).nice();
            this.yScale1.domain([0, d3Array.max(this.localData, (d: any): any => d.total)]).nice();
        }

        this.zScale.domain(this.keys);
        this.gpeChart
            .append('g')
            .selectAll('g')
            .data(d3Shape.stack().keys(this.keys)(this.localData))
            .enter()
            .append('g')
            .attr('fill', (d: any): unknown => this.zScale(d.key))
            .selectAll('rect')
            .data((d: any): any => d)
            .enter()
            .append('rect')
            .attr('x', (d: any): number => this.xScale(this.chartingService.config.xAccessor(d.data.x)))
            .attr('y', (d: any): number => this.yScale(d[1]))
            .attr('height', (d: any): number => this.yScale(d[0]) - this.yScale(d[1]))
            .attr('width', this.xScale.bandwidth());
        this.gpeChart.append('g')
            .attr('class', 'axis')
            .attr('transform', `translate(0,${this.dimensions.boundedHeight})`)
            .call(d3Axis.axisBottom(this.xScale).ticks(1, '%d/%m'));

        this.gpeChart.append('g')
            .attr('class', 'axis')
            .call(d3Axis.axisLeft(this.yScale1).ticks(5, this.fmt));
    }

    onHovered (isHovered: boolean): void
    {
        this.hovered = isHovered;
    }

    onClickEvent = (event: ICoordinates): void =>
    {
        this.columnClicked.emit(this.localData[this.getIdxBand(event.x)]);
    };

    onMousePosition = ($event: any): void =>
    {
        const idxBand = this.getIdxBand($event[0]);
        const currLocalData = this.localData[idxBand];

        this.tooltipData = new TooltipData(this.chartingService.config.xAccessor(currLocalData.x));
        this.tooltipData.posX = this.xScale(this.xScale.domain()[idxBand]) + this.eachBand;
        this.tooltipData.posY = this.dimensions.boundedHeight * 0.5;
        // if (currLocalData.total !== 0)
        // {
        //     this.tooltipData.posY = this.yScale(currLocalData.total) * 0.5;
        // }
        // else
        // {
        //     this.tooltipData.posY = this.dimensions.boundedHeight * 0.5;
        // }

        this.series.forEach((serie: Serie): number =>
            this.tooltipData.values.push(new TooltipValue(serie.label, serie.config.color, serie.dataPoints[idxBand].value))
        );
    };

    private getIdxBand (x: number): number
    {
        let idxBand = Math.floor(((x - this.dimensions.marginLeft) / this.eachBand));

        idxBand = Math.min(idxBand, this.series[0].dataPoints.length - 1);

        return idxBand;
    }

    protected initSvg (): void
    {
        this.baseInitSvg();
        this.chartingService.dimensions = this.dimensions;

        this.xScale = this.chartingService.getXScaleBand();
        this.yScale = this.chartingService.getYScaleLinear();
        this.yScale1 = this.chartingService.getYScaleLinear();
        this.zScale = this.chartingService.getZScaleOrdinal(this.data.colors);
    }
}
